import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Card, Checkbox, FormControlLabel, MenuItem, Stack,
  TextField, Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { occupantsApi, packagesApi } from "../api/resources";
import { useSettings } from "../context/SettingsContext";
import { useLanguage } from "../i18n/LanguageContext";
import { describeNotifyResult } from "../utils/notify";
import OccupantPicker from "../components/OccupantPicker";
import PhotoInput from "../components/PhotoInput";

export default function RegistrarPage() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { t } = useLanguage();
  const couriers = settings?.couriers || ["Otro"];

  const [occupant, setOccupant] = useState(null);
  const [courier, setCourier] = useState(couriers[0] || "Otro");
  const [courierNote, setCourierNote] = useState("");
  const [tracking, setTracking] = useState("");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [notifyAll, setNotifyAll] = useState(false);
  const [sameUnitOthers, setSameUnitOthers] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (couriers.length && !courier) setCourier(couriers[0]);
  }, [couriers]); // eslint-disable-line

  useEffect(() => {
    if (!occupant) {
      setSameUnitOthers([]);
      return;
    }
    occupantsApi.list(occupant.unit).then((list) =>
      setSameUnitOthers(list.filter((o) => o.unit === occupant.unit && o._id !== occupant._id))
    );
  }, [occupant]);

  const isOtro = courier === "Otro";

  const handleSubmit = async () => {
    if (!occupant) return setError(t("registrar.errNoOccupant"));
    if (!desc.trim()) return setError(t("registrar.errNoDesc"));
    if (isOtro && !courierNote.trim()) return setError(t("registrar.errNoOtroNote"));

    setError("");
    setSaving(true);
    try {
      const result = await packagesApi.create({
        occupantId: occupant._id,
        courier,
        courierNote: isOtro ? courierNote.trim() : "",
        tracking,
        desc,
        photos,
        notifyAll,
      });
      navigate("/panel", {
        state: {
          toast: `${result.notify?.sent ? t("registrar.registeredAndNotified") : t("registrar.registeredNoNotify", "")} ${describeNotifyResult(result.notify, t)}`,
        },
      });
    } catch (err) {
      setError(err.response?.data?.error || t("registrar.registeredNoNotify", ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5">{t("registrar.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("registrar.subtitle")}
      </Typography>

      <Card sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <OccupantPicker value={occupant} onChange={setOccupant} />
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label={t("registrar.courierLabel")} fullWidth value={courier} onChange={(e) => setCourier(e.target.value)}>
            {couriers.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("registrar.trackingLabel")}
            fullWidth
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder={t("registrar.trackingPlaceholder")}
          />
        </Stack>

        {isOtro && (
          <TextField
            label={t("registrar.otroLabel")}
            fullWidth
            value={courierNote}
            onChange={(e) => setCourierNote(e.target.value)}
            placeholder={t("registrar.otroPlaceholder")}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          label={t("registrar.descLabel")}
          fullWidth
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder={t("registrar.descPlaceholder")}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <PhotoInput value={photos} onChange={setPhotos} label={t("registrar.photosLabel")} />
        </Box>

        {occupant && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("registrar.notifyInfoEmail", occupant.altEmail || occupant.email)}
            {occupant.whatsapp ? t("registrar.notifyInfoWhatsapp", occupant.whatsapp) : t("registrar.notifyInfoNoWhatsapp")}.
          </Alert>
        )}

        {occupant && sameUnitOthers.length > 0 && (
          <FormControlLabel
            control={<Checkbox checked={notifyAll} onChange={(e) => setNotifyAll(e.target.checked)} />}
            label={t("registrar.notifyAllLabel", occupant.unit, sameUnitOthers.map((o) => o.name).join(", "))}
            sx={{ mb: 1 }}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} disabled={saving}>
          {saving ? t("registrar.submitting") : t("registrar.submit")}
        </Button>
      </Card>
    </Box>
  );
}
